import { useState, useEffect } from 'react';
import {
  Stack,
  Text,
  Button,
  Alert,
  Group,
  Switch,
  Select,
} from '@mantine/core';
import { TimeInput } from '@mantine/dates';
import {
  IconCheck,
  IconAlertCircle,
  IconInfoCircle,
} from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import { useAuth } from '../../hooks/useAuth';
import { useFeatureFlag } from '../../hooks/useFeatureFlags';
import { CalendarPreferences } from '../../types/familyProfile';
import { initiateGoogleCalendarConnection, disconnectGoogleCalendar } from '../../utils/googleCalendarAuth';

type GoogleCalendarPreferenceProps = {
  value: {
    googleCalendarConnected?: boolean;
    calendarPreferences?: CalendarPreferences;
  };
  onChange: (value: {
    googleCalendarConnected?: boolean;
    calendarPreferences?: CalendarPreferences;
  }) => void;
};

const SHOPPING_DAYS_OPTIONS = [
  { value: '1', label: '1 day before' },
  { value: '2', label: '2 days before' },
  { value: '3', label: '3 days before' },
  { value: '4', label: '4 days before' },
  { value: '5', label: '5 days before' },
  { value: '6', label: '6 days before' },
  { value: '7', label: '7 days before' },
];

const THAW_OPTIONS = [
  { value: '120', label: '2 hours' },
  { value: '240', label: '4 hours' },
  { value: '480', label: '8 hours' },
  { value: '720', label: '12 hours' },
  { value: '1440', label: '24 hours' },
  { value: '2880', label: '48 hours' },
];

const MARINATE_OPTIONS = [
  { value: '30', label: '30 minutes' },
  { value: '60', label: '1 hour' },
  { value: '120', label: '2 hours' },
  { value: '240', label: '4 hours' },
  { value: '480', label: '8 hours' },
];

const DEFAULT_PREFERENCES: CalendarPreferences = {
  enabled: true,
  mealEvents: true,
  prepReminders: true,
  shoppingReminders: true,
  defaultMealTime: '18:00',
  reminderLeadTimes: { thawMinutes: 1440, marinateMinutes: 240 },
};

export const GoogleCalendarPreference = ({ value, onChange }: GoogleCalendarPreferenceProps) => {
  const { user } = useAuth();
  const additionalMeals = useFeatureFlag('additional_meals');
  const [isConnecting, setIsConnecting] = useState(false);
  const [isDisconnecting, setIsDisconnecting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const connected = value.googleCalendarConnected ?? false;
  const preferences = value.calendarPreferences ?? DEFAULT_PREFERENCES;

  const updatePreference = (updates: Partial<CalendarPreferences>) => {
    onChange({ calendarPreferences: { ...preferences, ...updates } });
  };

  // Check for OAuth callback parameters
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const calendarStatus = urlParams.get('google_calendar');
    const errorParam = urlParams.get('error');

    if (calendarStatus === 'connected' && !connected) {
      onChange({ googleCalendarConnected: true, calendarPreferences: DEFAULT_PREFERENCES });
      const url = new URL(window.location.href);
      url.searchParams.delete('google_calendar');
      window.history.replaceState({}, '', url.toString());
      notifications.show({
        title: 'Google Calendar connected',
        message: 'Your Google Calendar is now connected! Configure your sync preferences below.',
        color: 'green',
        icon: <IconCheck size={18} />,
      });
    }

    if (errorParam === 'google_calendar_auth_failed' || errorParam === 'google_calendar_no_refresh_token') {
      setError(
        errorParam === 'google_calendar_no_refresh_token'
          ? 'Google Calendar connection failed. Please try again and make sure to grant offline access.'
          : 'Failed to connect Google Calendar. Please try again.'
      );
      const url = new URL(window.location.href);
      url.searchParams.delete('error');
      window.history.replaceState({}, '', url.toString());
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleConnect = async () => {
    if (!user) return;
    setIsConnecting(true);
    setError(null);
    try {
      await initiateGoogleCalendarConnection(user);
    } catch {
      setError('Failed to connect to Google Calendar. Please try again.');
      setIsConnecting(false);
    }
  };

  const handleDisconnect = async () => {
    if (!user) return;
    setIsDisconnecting(true);
    try {
      await disconnectGoogleCalendar(user);
      onChange({ googleCalendarConnected: false });
      notifications.show({
        title: 'Google Calendar disconnected',
        message: 'Your Google Calendar has been disconnected.',
        color: 'blue',
        icon: <IconCheck size={18} />,
      });
    } catch {
      notifications.show({
        title: 'Error',
        message: 'Failed to disconnect Google Calendar. Please try again.',
        color: 'red',
        icon: <IconAlertCircle size={18} />,
      });
    } finally {
      setIsDisconnecting(false);
    }
  };

  if (!connected) {
    return (
      <Stack gap="md">
        {error && (
          <Alert icon={<IconAlertCircle size={16} />} color="red" variant="light" radius="lg">
            {error}
          </Alert>
        )}
        <Alert icon={<IconInfoCircle size={16} />} color="blue" variant="light" radius="lg">
          Connect your Google Calendar to automatically sync meal plan events, prep reminders, and shopping reminders to your calendar.
        </Alert>
        <Button
          color="blue"
          size="sm"
          w={{ base: '100%', sm: '260px' }}
          style={{ alignSelf: 'center' }}
          onClick={handleConnect}
          loading={isConnecting}
        >
          Connect Google Calendar
        </Button>
      </Stack>
    );
  }

  return (
    <Stack gap="md">
      <Alert icon={<IconCheck size={16} />} color="green" variant="light" radius="lg">
        Your Google Calendar is connected! Events sync to a "Meals You Love" calendar created automatically in your Google Calendar.
      </Alert>

      <Stack gap="lg">
        {/* Master toggle */}
        <Group justify="space-between">
          <div>
            <Text fw={500} size="sm">Calendar sync</Text>
            <Text size="xs" c="dimmed">Enable or disable all calendar syncing</Text>
          </div>
          <Switch
            checked={preferences.enabled}
            onChange={(e) => updatePreference({ enabled: e.currentTarget.checked })}
          />
        </Group>

        {preferences.enabled && (
          <>
            {/* Event type toggles */}
            <Stack gap="sm">
              <Text fw={500} size="sm">Events to sync</Text>
              <Group justify="space-between">
                <div>
                  <Text size="sm">Meal events</Text>
                  {/* Flag-on, this toggle covers every cooking session, not
                      just dinners; flag-off keeps the original copy */}
                  <Text size="xs" c="dimmed">
                    {additionalMeals
                      ? 'Cooking events with recipe details'
                      : 'Dinner events with recipe details'}
                  </Text>
                </div>
                <Switch
                  checked={preferences.mealEvents}
                  onChange={(e) => updatePreference({ mealEvents: e.currentTarget.checked })}
                />
              </Group>
              <Group justify="space-between">
                <div>
                  <Text size="sm">Prep reminders</Text>
                  <Text size="xs" c="dimmed">Thaw meat, start marinating</Text>
                </div>
                <Switch
                  checked={preferences.prepReminders}
                  onChange={(e) => updatePreference({ prepReminders: e.currentTarget.checked })}
                />
              </Group>
              <Group justify="space-between">
                <div>
                  <Text size="sm">Shopping reminders</Text>
                  <Text size="xs" c="dimmed">Reminder to shop before plan starts</Text>
                </div>
                <Switch
                  checked={preferences.shoppingReminders}
                  onChange={(e) => updatePreference({ shoppingReminders: e.currentTarget.checked })}
                />
              </Group>
            </Stack>

            {/* Shopping reminder timing */}
            {preferences.shoppingReminders && (
              <Stack gap="sm">
                <Text fw={500} size="sm">Shopping reminder timing</Text>
                <Group justify="space-between" align="flex-end">
                  <div>
                    <Text size="sm">When to remind</Text>
                    <Text size="xs" c="dimmed">How far before the plan starts</Text>
                  </div>
                  <Select
                    data={SHOPPING_DAYS_OPTIONS}
                    value={String(preferences.shoppingReminderDaysBefore ?? 1)}
                    onChange={(val) => {
                      if (val) {
                        updatePreference({ shoppingReminderDaysBefore: Number(val) });
                      }
                    }}
                    w={160}
                  />
                </Group>
                <Group justify="space-between" align="flex-end">
                  <div>
                    <Text size="sm">Reminder time</Text>
                    <Text size="xs" c="dimmed">What time of day</Text>
                  </div>
                  <TimeInput
                    value={preferences.shoppingReminderTime || '10:00'}
                    onChange={(e) => {
                      const val = e.currentTarget.value;
                      if (val) {
                        updatePreference({ shoppingReminderTime: val });
                      }
                    }}
                    w={120}
                  />
                </Group>
              </Stack>
            )}

            {/* Breakfast/lunch times: only meaningful when those meals can
                be planned, so flag-off settings stay byte-identical. Placed
                before dinner so flag-on reads in day order. */}
            {additionalMeals && (
              <>
                <Stack gap="xs">
                  <Text fw={500} size="sm">Breakfast time</Text>
                  <Text size="xs" c="dimmed">When breakfasts land on your calendar</Text>
                  <TimeInput
                    value={preferences.breakfastTime || '07:30'}
                    onChange={(e) => {
                      const val = e.currentTarget.value;
                      if (val) {
                        updatePreference({ breakfastTime: val });
                      }
                    }}
                    w={120}
                  />
                </Stack>
                <Stack gap="xs">
                  <Text fw={500} size="sm">Lunch time</Text>
                  <Text size="xs" c="dimmed">When lunches land on your calendar</Text>
                  <TimeInput
                    value={preferences.lunchTime || '12:00'}
                    onChange={(e) => {
                      const val = e.currentTarget.value;
                      if (val) {
                        updatePreference({ lunchTime: val });
                      }
                    }}
                    w={120}
                  />
                </Stack>
              </>
            )}

            {/* Default meal time */}
            <Stack gap="xs">
              <Text fw={500} size="sm">Dinner time</Text>
              {/* Flag-on, this hour also places made-ahead batch cooking on
                  its cook day, which is otherwise undiscoverable */}
              <Text size="xs" c="dimmed">
                {additionalMeals
                  ? 'Cooking is scheduled backward from this so dinner is ready on time. Batch cooking lands at this hour on its cook day.'
                  : 'Calendar events are scheduled backward from this so dinner is ready on time'}
              </Text>
              <TimeInput
                value={preferences.defaultMealTime || '18:00'}
                onChange={(e) => {
                  const val = e.currentTarget.value;
                  if (val) {
                    updatePreference({ defaultMealTime: val });
                  }
                }}
                w={120}
              />
            </Stack>

            {/* Prep reminder timing */}
            {preferences.prepReminders && (
              <Stack gap="sm">
                <Text fw={500} size="sm">Prep reminder timing</Text>
                <Group justify="space-between" align="flex-end">
                  <div>
                    <Text size="sm">Thaw reminder</Text>
                    <Text size="xs" c="dimmed">How far in advance to remind about thawing</Text>
                  </div>
                  <Select
                    data={THAW_OPTIONS}
                    value={String(preferences.reminderLeadTimes?.thawMinutes ?? 1440)}
                    onChange={(val) => {
                      if (val) {
                        updatePreference({
                          reminderLeadTimes: {
                            thawMinutes: Number(val),
                            marinateMinutes: preferences.reminderLeadTimes?.marinateMinutes ?? 240,
                          },
                        });
                      }
                    }}
                    w={140}
                  />
                </Group>
                <Group justify="space-between" align="flex-end">
                  <div>
                    <Text size="sm">Marinate reminder</Text>
                    <Text size="xs" c="dimmed">How far in advance to remind about marinating</Text>
                  </div>
                  <Select
                    data={MARINATE_OPTIONS}
                    value={String(preferences.reminderLeadTimes?.marinateMinutes ?? 240)}
                    onChange={(val) => {
                      if (val) {
                        updatePreference({
                          reminderLeadTimes: {
                            thawMinutes: preferences.reminderLeadTimes?.thawMinutes ?? 1440,
                            marinateMinutes: Number(val),
                          },
                        });
                      }
                    }}
                    w={140}
                  />
                </Group>
              </Stack>
            )}
          </>
        )}
      </Stack>

      <Button
        variant="subtle"
        color="red"
        size="sm"
        style={{ alignSelf: 'center' }}
        onClick={handleDisconnect}
        loading={isDisconnecting}
      >
        Disconnect Google Calendar
      </Button>
    </Stack>
  );
};
